import {CacheKeyConfig} from '@aboutyou/data-fetcher';
import {
  useHistory,
  useParams,
  useParamsCallback,
  usePathname,
} from '@aboutyou/router';
import {useCallback} from 'react';

export const useCategoryIdentifier = () => {
  const pathname = usePathname();
  const {categoryId} = useParams<{categoryId?: string}>();

  const id = Number(categoryId);

  if (!isNaN(id)) {
    return {
      $case: 'id' as const,
      id,
    };
  }

  // handles the case where we are on a modern url but the id is missing in the path
  const legacyPathname = modernToLegacyPathname(pathname);
  if (legacyPathname) {
    return {
      $case: 'legacyPath' as const,
      legacyPath: legacyPathname,
    };
  }

  return {
    $case: 'legacyPath' as const,
    legacyPath: pathname,
  };
};

export const useCategoryIdentifierCallback = () => {
  const {location} = useHistory();
  const getCategory = useParamsCallback<{categoryId?: string}>();
  return useCallback(() => {
    const pathname = location.pathname;
    const {categoryId} = getCategory();

    const id = Number(categoryId);

    if (!isNaN(id)) {
      return {
        $case: 'id' as const,
        id,
      };
    }

    // handles the case where we are on a modern url but the id is missing in the path
    const legacyPathname = modernToLegacyPathname(pathname);
    if (legacyPathname) {
      return {
        $case: 'legacyPath' as const,
        legacyPath: legacyPathname,
      };
    }

    return {
      $case: 'legacyPath' as const,
      legacyPath: pathname,
    };
  }, [location]);
};

/**
 * Legacy url format: `/<categoryPath>`
 * Modern url format: `/c/<categoryPath>-<categoryId>`
 * Modern url without id format: `/c/<categoryPath>`
 */
const modernToLegacyPathname = (modernPathname: string) => {
  const matchFullId = modernPathname.match(/^\/c\/(.+)-\d/);
  const matchNoId = modernPathname.match(/^\/c\/(.+)/);

  const result = matchFullId?.[1] || matchNoId?.[1];

  if (!result) {
    return undefined;
  }

  return `/${result}`;
};

/**
 * Returns, when a modern path is detected in the url, the legacy version of it. If no matching pathname is found, it will return nothing.
 */
export const useLegacyPath = () => {
  const pathname = usePathname();
  return modernToLegacyPathname(pathname);
};

/**
 * Returns a function that unifies the category identifier on a category page request so that we don't create multiple requests for legacy and modern category urls. This is important as a lot of urls will still point to the legacy urls and we perform a redirect in the client.
 * Legacy url format: `/<categoryPath>`
 * Modern url format: `/c/<categoryPath>-<categoryId>`
 */
export const useCategoryCacheKeyTransformer = () => {
  const pathname = usePathname();
  const categoryIdentifier = useCategoryIdentifier();

  const legacyIdentifier =
    modernToLegacyPathname(pathname) ?? categoryIdentifier.legacyPath;

  return useCallback(
    (config: CacheKeyConfig) => {
      return {
        ...config,
        params: {
          ...config.params,
          category: {
            identifier: legacyIdentifier,
          },
        },
      };
    },
    [legacyIdentifier],
  );
};
